// libs
import React, { useCallback, useEffect, useRef, useState, memo } from 'react';

// components
import { SHEET_TRANSITION, OverlaySheetContext } from 'shared/components/OverlaySheet';
import { DEVICE_TYPE, getDeviceType } from 'shared/helpers/app';

// context
import { useLoaderContext } from 'shared/components/LoaderProvider/context';

// services
import { fetchEcomPolicyData as _fetchEcomPolicyData } from 'shared/store/details/services';

// styles
import { OverlaySheet } from './styles';
import {
  FixedContent as DesktopFixedContent,
  IconContainer as DesktopIconContainer,
  OverlaySheet as DesktopOverlaySheet,
  Wrapper as DesktopWrapper,
} from 'shared/layouts/DetailsPage/components/ProductInfo/Desktop/components/NewEcomPolicy/components/ContentWrapper/styles';

// type
import { IEcomPolicyData, IEcomPolicyKey } from 'shared/store/details/types';

// icons
import { useStoreIcon, IconName } from 'shared/icons';

// Helpers
import sanitizeHTML from 'shared/helpers/utils/sanitizeHTML';
// constants
import { ROLES, TAB_INDEX } from 'shared/constants';

interface IEcomPolicyModalProps {
  productId: number;
  selectedItem: IEcomPolicyKey;
  modalOpen: boolean;
  onClose: () => void;
  ecomPolicyData: IEcomPolicyData;
  fetchEcomPolicyData: typeof _fetchEcomPolicyData;
  isEComAPIError: boolean;
  isEComAPIFetching: boolean;
  vendorCode?: string;
  vendorType?: string;
}

function EcomPolicyModal({
  productId,
  selectedItem,
  modalOpen,
  onClose,
  ecomPolicyData,
  fetchEcomPolicyData,
  isEComAPIError,
  isEComAPIFetching,
  vendorCode,
  vendorType,
}: IEcomPolicyModalProps) {
  const [data, setData] = useState('');
  const fetchedVendorPolicy = useRef('');
  const { showLoader, hideLoader } = useLoaderContext();
  const CloseIconComponent = useStoreIcon(IconName.CLOSE);
  const vendorPolicyKey = `${productId}-${selectedItem}-${vendorCode}-${vendorType}`;

  const getData = useCallback(() => {
    // if there in redux return
    if (ecomPolicyData[selectedItem] && !vendorCode) {
      setData(ecomPolicyData[selectedItem]);
      return;
    }

    if (vendorCode && fetchedVendorPolicy.current === vendorPolicyKey) {
      setData(ecomPolicyData[selectedItem] || '');
      return;
    }

    // calling the api
    fetchedVendorPolicy.current = vendorPolicyKey;
    fetchEcomPolicyData(productId, selectedItem, vendorCode, vendorType);
  }, [
    ecomPolicyData,
    fetchEcomPolicyData,
    productId,
    selectedItem,
    vendorCode,
    vendorPolicyKey,
    vendorType,
  ]);

  useEffect(() => {
    if (productId && selectedItem) {
      getData();
    }

    return () => {
      setData('');
    };
  }, [getData, productId, selectedItem]);

  const ecomPolicyHtml = data !== '' ? data : '';
  const isDesktop = getDeviceType() === DEVICE_TYPE.DESKTOP;

  if (isEComAPIFetching) {
    showLoader({});
    return null;
  } else hideLoader();

  if (modalOpen === false || (isEComAPIError && !ecomPolicyHtml)) {
    return null;
  }

  if (isDesktop) {
    return (
      <DesktopOverlaySheet
        navKey="ecom-policy-dweb"
        onHide={onClose}
        transitionFrom={SHEET_TRANSITION.RIGHT}
        disableNav
      >
        <OverlaySheetContext.Consumer>
          {({ doHide }) => (
            <DesktopFixedContent>
              <DesktopWrapper>
                <DesktopIconContainer
                  role={ROLES.BUTTON}
                  tabIndex={TAB_INDEX.ACTION_INVISIBILITY}
                  onKeyDown={doHide}
                  onClick={doHide}
                >
                  <CloseIconComponent />
                </DesktopIconContainer>
                <div dangerouslySetInnerHTML={{ __html: sanitizeHTML(ecomPolicyHtml) }} />
              </DesktopWrapper>
            </DesktopFixedContent>
          )}
        </OverlaySheetContext.Consumer>
      </DesktopOverlaySheet>
    );
  }

  return (
    <OverlaySheet
      navKey={'ecom-policy'}
      onHide={onClose}
      transitionFrom={SHEET_TRANSITION.BOTTOM}
      disableNav
    >
      <OverlaySheetContext.Consumer>
        {({ doHide }) => {
          return (
            <div className="fixed-content">
              <div className="content-wrapper">
                <div
                  role={ROLES.BUTTON}
                  tabIndex={TAB_INDEX.ACTION_INVISIBILITY}
                  className="close-btn"
                  onKeyDown={doHide}
                  onClick={doHide}
                >
                  <CloseIconComponent />
                </div>
                <div dangerouslySetInnerHTML={{ __html: sanitizeHTML(ecomPolicyHtml) }} />
              </div>
            </div>
          );
        }}
      </OverlaySheetContext.Consumer>
    </OverlaySheet>
  );
}
export default memo(EcomPolicyModal);
